/**
 * Canonical gateway policy schemas — single source of truth.
 *
 * These mirror `openapi.Policy` / `PolicyResponse` field for field, with one
 * addition: `type`. The API discriminates variants by which of `firewall`
 * or `logging` is set, which zod cannot switch on, so `fromWirePolicy`
 * attaches `type` on read. `type` and the server-owned `id` are dropped
 * again by the SDK's own outbound schema, which ignores unknown keys.
 *
 * Add a policy type by extending the union below and wiring it through
 * `fromWirePolicy`.
 */
import { z } from "zod";

// ── Limits ──────────────────────────────────────────────────────────────

// Pre-flight only: the generated SDK carries none of the spec's size bounds, so
// these drive inline form errors and "N / max" counters instead of a server 400.
// Keep each in step with its counterpart under svc/api/openapi/spec. Too tight
// and a stored policy the API accepts fails to open for editing; too loose and
// the save 400s anyway.
export const POLICY_LIMITS = {
  maxPolicies: 50,
  maxNameLength: 256,
  maxMatchExprsPerPolicy: 10,
  maxRemoteIpEntries: 100,
} as const;

// ── String match (protojson oneof: exact | prefix | regex) ──────────────

export const stringMatchModeSchema = z.enum(["exact", "prefix", "regex"]);
export type StringMatchMode = z.infer<typeof stringMatchModeSchema>;

const stringMatchBase = { ignoreCase: z.boolean().optional() } as const;
const stringMatchValue = z.string().min(1);

export const stringMatchSchema = z.union([
  z.object({ ...stringMatchBase, exact: stringMatchValue }).strict(),
  z.object({ ...stringMatchBase, prefix: stringMatchValue }).strict(),
  z.object({ ...stringMatchBase, regex: stringMatchValue }).strict(),
]);
export type StringMatch = z.infer<typeof stringMatchSchema>;

// ── Match expressions (protojson oneof: path | method | header | queryParam | remoteIp)

const remoteIpListSchema = z.array(z.string().min(1)).min(1).max(POLICY_LIMITS.maxRemoteIpEntries);

const httpMethod = z.enum(["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"]);

export const matchExprSchema = z.union([
  z.object({ path: z.object({ path: stringMatchSchema }).strict() }).strict(),
  z
    .object({
      method: z.object({ methods: z.array(httpMethod).min(1) }).strict(),
    })
    .strict(),
  z
    .object({
      header: z
        .object({ name: z.string().min(1) })
        .and(
          z.union([z.object({ present: z.literal(true) }), z.object({ value: stringMatchSchema })]),
        ),
    })
    .strict(),
  z
    .object({
      queryParam: z
        .object({ name: z.string().min(1) })
        .and(
          z.union([z.object({ present: z.literal(true) }), z.object({ value: stringMatchSchema })]),
        ),
    })
    .strict(),
  z
    .object({
      remoteIp: z.union([
        z.object({ in: remoteIpListSchema }).strict(),
        z.object({ notIn: remoteIpListSchema }).strict(),
      ]),
    })
    .strict(),
]);
export type MatchExpr = z.infer<typeof matchExprSchema>;

// ── Common policy fields ────────────────────────────────────────────────

const policyBase = {
  id: z.string().min(1),
  name: z.string().min(1),
  enabled: z.boolean(),
  match: z.array(matchExprSchema).max(POLICY_LIMITS.maxMatchExprsPerPolicy).optional(),
} as const;

// ── Firewall policy ─────────────────────────────────────────────────────

// Wire values match frontline.v1.Action enum names. Kept as string literals so
// protojson round-trips them by name rather than numeric value. The MVP only
// has ACTION_DENY; the enum exists so additional outcomes can land later
// without changing the schema shape.
export const firewallActionSchema = z.enum(["ACTION_DENY"]);
export type FirewallAction = z.infer<typeof firewallActionSchema>;

export const firewallPolicySchema = z
  .object({
    ...policyBase,
    type: z.literal("firewall"),
    firewall: z
      .object({
        action: firewallActionSchema,
      })
      .strict(),
  })
  .strict();

export type FirewallPolicy = z.infer<typeof firewallPolicySchema>;

// ── Logging policy ──────────────────────────────────────────────────────

// Opts matching requests into capturing more request data in the request
// log (Requests tab). The gateway always records a base log entry per
// request (method, host, path, status, latency) — that cannot be turned
// off. This policy adds five independent captures on top: request headers
// (with user agent and client IP), response headers, request body,
// response body, and query data (query string and parameters). An empty
// `match` list matches every request, so a policy without match conditions
// captures the extras for all traffic.
// All fields are optional because protojson omits false booleans.
export const loggingPolicySchema = z
  .object({
    ...policyBase,
    type: z.literal("logging"),
    logging: z
      .object({
        requestHeaders: z.boolean().optional(),
        responseHeaders: z.boolean().optional(),
        requestBody: z.boolean().optional(),
        responseBody: z.boolean().optional(),
        query: z.boolean().optional(),
      })
      .strict(),
  })
  .strict();
export type LoggingPolicy = z.infer<typeof loggingPolicySchema>;

// ── Gateway policy (discriminated union — extend with new types here) ──

export const policySchema = z.discriminatedUnion("type", [
  firewallPolicySchema,
  loggingPolicySchema,
]);
export type Policy = z.infer<typeof policySchema>;
export type PolicyType = Policy["type"];

/** A trailing space is invisible once rendered, so it cannot make a second policy. */
export function normalizePolicyName(name: string): string {
  return name.trim();
}

/**
 * What makes two policies the same policy across environments. Type belongs in
 * the match key because a name can repeat across types: a firewall and a
 * logging policy both called "Guard" are two policies, not one.
 */
export function policyMatchKey(type: PolicyType, name: string): string {
  return `${type}:${normalizePolicyName(name)}`;
}

/** Attaches the `type` discriminator the API leaves implicit. */
export function fromWirePolicy(raw: unknown): Policy {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("policy must be an object");
  }
  const obj: Record<string, unknown> = { ...(raw as Record<string, unknown>) };
  if ("firewall" in obj) {
    return policySchema.parse({ ...obj, type: "firewall" });
  }
  if ("logging" in obj) {
    return policySchema.parse({ ...obj, type: "logging" });
  }
  throw new Error("unknown gateway policy variant");
}
